import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { useMyPaymentTerms } from '../api/hooks';
import { Alert } from '../ui';
import { AuthPage } from './auth-parts';

/**
 * ON-12 — the one screen a retailer who has not yet accepted trading terms
 * sees, once `mustChangePassword` has cleared. `RetailerGuard` refuses every
 * other retailer route while `termsAcceptedAt` is null; this is the
 * client-side experience of that gate, not the gate itself.
 */
export function AcceptTerms() {
  const { t } = useTranslation();
  const { me, refreshMe, signOut } = useSession();
  /*
   * A revision awaiting acceptance still gates the portal — that part is unchanged. What is
   * gone is the SCHEDULE it used to display: `stages` fed the M1-M4 table, retired at v1.6.
   * The pending record itself still decides whether this screen appears and carries the
   * reason Miraki gave for the change.
   *
   * `mine` is read only to tell "no terms set yet" apart from "still loading" — not to draw
   * a schedule. Two sessions reached the same screen from different directions in the same
   * week: main stopped ASSUMING a default 30/30/30/10, this branch removed the staged model
   * the default belonged to (§1.6). Both halves are kept — no stage table, and an honest
   * answer when an account has no terms yet, because under v1.6 payment terms are still real
   * as net days from invoice even though the stages are not.
   */
  const mine = useMyPaymentTerms();
  const pending = me?.kind === 'retailer' ? me.paymentTermsPending : null;
  // Someone who has already accepted the trading terms is being asked about a CHANGE.
  const revised = pending !== null && me?.kind === 'retailer' && me.termsAcceptedAt !== null;
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/accept-terms', pending ? { paymentTermsVersion: pending.version } : {});
      await refreshMe();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.acceptTerms.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage
      eyebrow={t('auth.eyebrow')}
      title={revised ? t('auth.acceptTerms.revisedTitle') : t('auth.acceptTerms.title')}
      lede={revised ? t('auth.acceptTerms.revisedBody') : t('auth.acceptTerms.body')}
      wide
    >
      <form onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Alert tone="stop">{error}</Alert> : null}

        {/*
          * THE M1-M4 STAGE TABLE IS GONE — retired at v1.6, and this was the last place it
          * survived after A-02 and A-04.
          *
          * It mattered most here: this is the only one of the three a CUSTOMER sees, and it
          * was not merely displaying a stale schedule, it was asking them to AGREE to one.
          * A retailer accepted "30% before manufacturing begins" on a platform that
          * manufactures nothing against their order and takes no payment before dispatch
          * (D31/D34/F4) — pieces are consigned, and only a piece they have SOLD becomes
          * payable (§6.12).
          *
          * A reason from Miraki still shows, because a revision they are being asked to
          * accept should say why it was revised.
          */}
        {pending?.reason ? (
          <div className="mk-auth-field">
            <p className="mk-dim">{t('auth.acceptTerms.paymentTermsReason', { reason: pending.reason })}</p>
          </div>
        ) : mine.isSuccess ? (
          // No schedule is assumed: until an admin sets terms there is nothing to show or agree to.
          <div className="mk-auth-field">
            <b>{t('auth.acceptTerms.paymentTermsTitle')}</b>
            <p className="mk-dim">{t('auth.acceptTerms.paymentTermsNotSet')}</p>
          </div>
        ) : null}

        <div className="mk-auth-field">
          <b>{t('auth.acceptTerms.summaryTitle')}</b>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--mk-text-muted)' }}>
            {/* What the retailer is actually agreeing to under consignment. The first line
                used to promise "advance percentage, credit limit and delivery term" — three
                things v1.6 retired (D33, D37, and the Incoterm with SH-07), so it described
                an arrangement that no longer exists to somebody being asked to accept it. */}
            <li>{t('auth.acceptTerms.summaryConsignment')}</li>
            <li>{t('auth.acceptTerms.summaryBudget')}</li>
            <li>{t('auth.acceptTerms.summaryPaymentTerms')}</li>
            <li>{t('auth.acceptTerms.summaryPricing')}</li>
            <li>{t('auth.acceptTerms.summaryLedger')}</li>
          </ul>
        </div>

        <div className="mk-auth-field" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <input
            id="acceptTerms" type="checkbox" checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <label htmlFor="acceptTerms">{t('auth.acceptTerms.checkbox')}</label>
        </div>

        <button className="mk-auth-submit" type="submit" disabled={busy || !checked}>
          {busy ? t('auth.submitting') : t('auth.acceptTerms.submit')}
        </button>
      </form>

      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <button type="button" className="mk-auth-alt" onClick={() => void signOut()}>
          {t('common.signOut')}
        </button>
      </div>
    </AuthPage>
  );
}
